/**
 * Pre-baked conversation + bullet fixtures for the Margot demo.
 *
 * Two collections:
 *  - SCOPE_FIXTURES   keyed by requirement text  (ScopeBulletsModal)
 *  - COURSE_FIXTURES  keyed by `${courseCode}::${prompt}`  (CourseConversationModal)
 *
 * Each fixture: { messages: [{id, role, content, timestamp}], bullets: [...] }.
 * Bullet shape mirrors the live API: {id, type, text, scores, goalAlignment?}.
 * Scores total ≥13 reads as "strong" in the LightBulletCard treatment.
 *
 * If the user types after a fixture loads, the modal appends their message
 * + a generic acknowledgement turn — no backend call, demo doesn't stall.
 */

const STRONG = { specificity: 4, impact: 4, ownership: 4, structure: 3 }; // 15
const GOOD   = { specificity: 3, impact: 3, ownership: 3, structure: 2 }; // 11
const FAIR   = { specificity: 3, impact: 2, ownership: 2, structure: 2 }; //  9

const tNow = (offset = 0) => new Date(Date.now() - (3 - offset) * 1000).toISOString();

// ---- Scope Bullets fixtures (PMM requirements) ---------------------------

export const SCOPE_FIXTURES = {
  // Senior PMM · "Cross-functional partnership with Product, Sales, and Marketing"
  "Cross-functional partnership with Product, Sales, and Marketing": {
    messages: [
      {
        id: 'm-cf-1',
        role: 'assistant',
        content:
          "You've already exceeded the bar on this one — partnering across three businesses at Meta and partnering with engineering leadership at AWS is real, senior-flavored cross-functional work. Let's make it land for a PMM hiring manager.\n\nTell me about the most cross-functional initiative you've owned at Meta — the moment where the alignment work was the actual deliverable, not a side effect. Who were the stakeholders, what was at stake, and what did you actually have to do to bring it together?",
        timestamp: tNow(0),
      },
      {
        id: 'm-cf-2',
        role: 'user',
        content:
          "The 'Rolodex' framework rollout for Early-in-Career Research. I had to align three engineering business leads (AI infra, Robotics & HCI, Optics & Photonics), the broader recruiting org, and our internal Workplace community on a single tiered candidate-pool model. Each business had their own historical hiring signals and their own preferences — I ran weekly business syncs to keep priorities aligned, prototyped the framework, then drove adoption through Sourcing Shorts (the Workplace community I founded). The output: 158 intern accepts, 39% of total 2026 hires.",
        timestamp: tNow(1),
      },
      {
        id: 'm-cf-3',
        role: 'assistant',
        content:
          "Perfect — you've got the three things a senior PMM hiring manager wants to see: multi-stakeholder ownership, a deliverable they cared about, and a measured outcome. Drafting bullets now.",
        timestamp: tNow(2),
      },
    ],
    bullets: [
      {
        id: 'b-cf-1',
        type: 'STAR',
        text: "Aligned three engineering business leads (AI infrastructure, Robotics & HCI, Optics & Photonics) on a unified tiered candidate-pool model; ran weekly cross-business syncs and drove adoption through a 167-member internal community, delivering 158 intern accepts and 39% of total 2026 hires.",
        scores: STRONG,
        goalAlignment: { evidenceStrength: 'strong', goal: 'cross-functional partnership' },
      },
      {
        id: 'b-cf-2',
        type: 'CAR',
        text: "Owned cross-functional rollout of the Rolodex sourcing framework across three Meta research businesses — translated each org's distinct hiring signals into a single prioritization rubric and held weekly alignment with engineering leadership.",
        scores: GOOD,
        goalAlignment: { evidenceStrength: 'strong', goal: 'cross-functional partnership' },
      },
      {
        id: 'b-cf-3',
        type: 'APR',
        text: "Built and shipped the cross-org rollout playbook for talent-sourcing frameworks at Meta; partnered weekly with Eng leadership across three research businesses and the broader recruiting org.",
        scores: GOOD,
        goalAlignment: { evidenceStrength: 'medium', goal: 'cross-functional partnership' },
      },
    ],
  },

  // Senior PMM · "Customer research, segmentation, and JTBD frameworks"
  "Customer research, segmentation, and JTBD frameworks": {
    messages: [
      {
        id: 'm-seg-1',
        role: 'assistant',
        content:
          "Two strong angles in your background: the Rolodex segmentation work at Meta (tiered candidate pools by historical hiring signal) and the Conference Sourcing automation that mapped 18,000+ NeurIPS/CVPR/ICML authors. Both are PMM-shaped — segmentation is segmentation, customer or candidate.\n\nLet's start with Rolodex. How did you decide what tiers to use, and how did you validate that the rubric actually predicted outcomes?",
        timestamp: tNow(0),
      },
      {
        id: 'm-seg-2',
        role: 'user',
        content:
          "I started by mining historical hiring data — what schools, advisors, paper topics, and engagement patterns actually converted into accepts vs. who we contacted but never engaged. That gave me 4 tiers. I validated by back-testing: ran the rubric against the prior cycle's data and showed it would have correctly predicted ~80% of the actual accept pool with half the outreach effort. Then we rolled it forward — and 2026 came in at 158 accepts, 39% of total hires.",
        timestamp: tNow(1),
      },
      {
        id: 'm-seg-3',
        role: 'assistant',
        content:
          "Excellent — the back-testing methodology is what separates this from a 'we made some buckets' story. That's the JTBD/segmentation discipline a PMM hiring manager will recognize. Here are bullet drafts:",
        timestamp: tNow(2),
      },
    ],
    bullets: [
      {
        id: 'b-seg-1',
        type: 'STAR',
        text: "Designed a 4-tier candidate-segmentation framework ('Rolodex') for Meta Early-in-Career Research; back-tested against historical conversion data to validate predictive accuracy, then drove 158 intern accepts in 2026 — 39% of total hires.",
        scores: STRONG,
        goalAlignment: { evidenceStrength: 'strong', goal: 'segmentation & JTBD' },
      },
      {
        id: 'b-seg-2',
        type: 'CAR',
        text: "Built a tiered audience-segmentation rubric from historical signal data; back-test predicted ~80% of conversions at half the outreach cost, validated forward in a 158-accept hiring cycle.",
        scores: STRONG,
        goalAlignment: { evidenceStrength: 'strong', goal: 'segmentation & JTBD' },
      },
      {
        id: 'b-seg-3',
        type: 'STAR',
        text: "Mapped 18,000+ research authors from NeurIPS, CVPR, and ICML into actionable audience pools through automated author-mapping (50% manual-effort reduction); the same audience-research discipline that maps directly to PMM market segmentation.",
        scores: GOOD,
        goalAlignment: { evidenceStrength: 'medium', goal: 'segmentation & JTBD' },
      },
    ],
  },

  // Senior PMM · "Compelling written and verbal narrative skills"
  "Compelling written and verbal narrative skills": {
    messages: [
      {
        id: 'm-nar-1',
        role: 'assistant',
        content:
          "Your strongest narrative work is at Meta — representing Meta at ICCV to PhD-level researchers, plus the editorial cadence of Sourcing Shorts (167-member community, weekly posts). For a PMM bullet, the ICCV story is gold: technical audience, high stakes, narrative-as-deliverable.\n\nWalk me through what you actually said at ICCV and how you tailored it. What was different about how you positioned Meta to that crowd vs. how a generic recruiting pitch would land?",
        timestamp: tNow(0),
      },
      {
        id: 'm-nar-2',
        role: 'user',
        content:
          "PhD researchers at top vision conferences are deeply skeptical of generic 'come work at a big tech company' pitches — they care about specific research agendas, publishing freedom, and which labs they'd actually be working with. So I built a talk track that opened with the specific research areas Meta was hiring for (AI infra, Robotics, HCI, Optics), named the researchers leading each area, and explicitly addressed what publishing and conference travel looked like as a Meta researcher. Conversion from booth conversations into pipeline ran ~3x our prior conferences.",
        timestamp: tNow(1),
      },
      {
        id: 'm-nar-3',
        role: 'assistant',
        content:
          "That's the move — audience-specific narrative + measured conversion lift. Pure PMM craft. Drafting bullets:",
        timestamp: tNow(2),
      },
    ],
    bullets: [
      {
        id: 'b-nar-1',
        type: 'STAR',
        text: "Authored and delivered Meta's ICCV recruiting narrative for PhD-level vision researchers — tailored positioning around specific research areas, lab leadership, and publishing norms; tripled conversion-to-pipeline vs. prior conference cycles.",
        scores: STRONG,
        goalAlignment: { evidenceStrength: 'strong', goal: 'narrative & messaging' },
      },
      {
        id: 'b-nar-2',
        type: 'CAR',
        text: "Owned editorial calendar for a 167-member internal AI/automation community at Meta (Sourcing Shorts) — set the voice, ran weekly posts, and translated technical practice into shareable, repeatable content.",
        scores: GOOD,
        goalAlignment: { evidenceStrength: 'strong', goal: 'narrative & messaging' },
      },
    ],
  },

  // Senior PMM · "Sales enablement at scale (programs, training, certification)"
  "Sales enablement at scale (programs, training, certification)": {
    messages: [
      {
        id: 'm-en-1',
        role: 'assistant',
        content:
          "You're already building 'sales enablement' at Meta — just for a recruiter audience instead of a sales audience. The AI-Native training content you're authoring for the recruiting org is exactly the structure of a PMM enablement program.\n\nTell me about that work — what's the curriculum, who's the audience, and how are you measuring whether it's landing?",
        timestamp: tNow(0),
      },
      {
        id: 'm-en-2',
        role: 'user',
        content:
          "It's a multi-module curriculum for the broader Meta recruiting org — covers safe AI use, advanced prompting patterns, when to automate vs. when to keep humans in the loop, and practical workflows for sourcing, screening, and outreach. Audience is ~600 recruiters across business orgs. We're measuring adoption (% completing modules), self-reported confidence shifts, and concrete time-savings on a baseline set of recruiting tasks. Early data shows ~2 hours/week saved per recruiter once they complete the core modules.",
        timestamp: tNow(1),
      },
      {
        id: 'm-en-3',
        role: 'assistant',
        content:
          "That's a real enablement program — curriculum + audience definition + impact measurement. Translates directly to PMM sales enablement. Bullets:",
        timestamp: tNow(2),
      },
    ],
    bullets: [
      {
        id: 'b-en-1',
        type: 'STAR',
        text: "Built and rolled out an AI-Native enablement curriculum for ~600 Meta recruiters across business orgs; multi-module program covering safe AI use, advanced prompting, and concrete workflow integration — drove ~2 hours/week of measured time savings per certified recruiter.",
        scores: STRONG,
        goalAlignment: { evidenceStrength: 'strong', goal: 'sales enablement' },
      },
      {
        id: 'b-en-2',
        type: 'CAR',
        text: "Designed and shipped enablement training at org scale — defined the curriculum, set the certification path, and instrumented adoption + impact measurement across a 600-person internal audience.",
        scores: GOOD,
        goalAlignment: { evidenceStrength: 'strong', goal: 'sales enablement' },
      },
    ],
  },

  // Senior PMM · "Set positioning and messaging strategy" (partial — gap-side)
  "Set positioning and messaging strategy": {
    messages: [
      {
        id: 'm-pos-1',
        role: 'assistant',
        content:
          "This is one of your partial-fit rows — the role asks for product-positioning craft, and you've done positioning work but for a candidate audience, not a customer one. Let's surface what does transfer cleanly.\n\nWalk me through the message-multiplexing work at Meta — you've crafted hiring narratives across three distinct businesses (AI infra, Robotics & HCI, Optics & Photonics). How do you keep them differentiated?",
        timestamp: tNow(0),
      },
      {
        id: 'm-pos-2',
        role: 'user',
        content:
          "Each business has a distinct value prop for researchers — AI infra is 'work on the systems that train the next generation of models', Robotics & HCI is 'embodied research with real-world physical impact', Optics is 'foundational hardware for AR/VR.' So the same 'come work at Meta' umbrella has three sub-narratives, each tuned to what motivates that audience. I keep them differentiated by mapping each one against the actual research output and team culture — not abstract claims.",
        timestamp: tNow(1),
      },
      {
        id: 'm-pos-3',
        role: 'assistant',
        content:
          "Honest framing: this is the strongest signal you'll have for the positioning bar — message-multiplexing across audiences with grounded differentiation. It won't beat someone who's done this for products at scale, but it will land for a hiring manager looking for transferable craft. Drafting:",
        timestamp: tNow(2),
      },
    ],
    bullets: [
      {
        id: 'b-pos-1',
        type: 'STAR',
        text: "Crafted differentiated recruiting narratives across three Meta research businesses (AI infra, Robotics & HCI, Optics & Photonics) — each tuned to the audience's distinct motivators and grounded in actual research output rather than generic brand claims.",
        scores: GOOD,
        goalAlignment: { evidenceStrength: 'medium', goal: 'positioning & messaging' },
      },
      {
        id: 'b-pos-2',
        type: 'CAR',
        text: "Multiplexed positioning across three business-org audiences — built the framework that translated one umbrella value prop into three distinct, audience-specific narratives.",
        scores: FAIR,
        goalAlignment: { evidenceStrength: 'medium', goal: 'positioning & messaging' },
      },
    ],
  },
};

// ---- Course conversation fixtures ----------------------------------------

export const COURSE_FIXTURES = {
  // I S 500 · SQL Project — Max called this out specifically
  "I S 500::SQL Project": {
    messages: [
      {
        id: 'm-sql-1',
        role: 'assistant',
        content:
          "The SQL project is a great one to capture — hands-on data work translates directly to PMM analytics chops, and Foster I S 500 has a reputation for forcing real schema discipline.\n\nWhat was the project actually building toward? Was it a data-modeling exercise, an ETL pipeline, or analytical querying against a real dataset?",
        timestamp: tNow(0),
      },
      {
        id: 'm-sql-2',
        role: 'user',
        content:
          "Real-dataset project — we had three years of e-commerce transactions and had to build a query layer that supported a marketing analytics dashboard. So I built the dimensional model from raw fact tables, wrote about a dozen queries with windowing and CTEs for cohort analysis, and had to defend the schema design in a code-review session with the prof. It clicked for me when I realized the same cohort logic powers SaaS retention analysis — not just retail.",
        timestamp: tNow(1),
      },
      {
        id: 'm-sql-3',
        role: 'assistant',
        content:
          "That's the framing PMM hiring managers love — you connected the academic project to the business pattern (cohort analysis = retention analysis). Drafting bullets you can attach to your Foster MBA entry:",
        timestamp: tNow(2),
      },
    ],
    bullets: [
      {
        id: 'b-sql-1',
        type: 'STAR',
        text: "Built a dimensional data model and 12+ CTE / windowed-query analyses against 3 years of e-commerce transaction data; supported a downstream marketing analytics dashboard for cohort and conversion analysis (Foster I S 500 — Data Management for Analytics).",
        scores: STRONG,
        goalAlignment: { evidenceStrength: 'strong', goal: 'data fluency' },
      },
      {
        id: 'b-sql-2',
        type: 'CAR',
        text: "Owned schema design and analytical query layer for an end-to-end marketing analytics project; defended design choices in instructor code review and connected retail-cohort patterns to SaaS retention analogs.",
        scores: GOOD,
        goalAlignment: { evidenceStrength: 'strong', goal: 'data fluency' },
      },
    ],
  },

  // I S 500 · Final analytics project
  "I S 500::Final analytics project": {
    messages: [
      {
        id: 'm-fap-1',
        role: 'assistant',
        content:
          "Tell me about the final project — what data did you work with, what did you ultimately ship, and what surprised you about the result?",
        timestamp: tNow(0),
      },
    ],
    bullets: [],
  },

  // FIN 557 · Startup valuation case
  "FIN 557::Startup valuation case": {
    messages: [
      {
        id: 'm-val-1',
        role: 'assistant',
        content:
          "Startup valuation is a great PMM-adjacent skill — understanding why a company is worth what it's worth tells you what story marketing has to tell. Walk me through the case: what stage was the startup, what method did you use, and where did you land vs. the actual outcome?",
        timestamp: tNow(0),
      },
    ],
    bullets: [],
  },

  // B ECON 501 · Country/region case study
  "B ECON 501 C::Country/region case study": {
    messages: [
      {
        id: 'm-econ-1',
        role: 'assistant',
        content:
          "Macro country/region work translates well for PMM — understanding regional appetite for a product category is half of go-to-market. Which country or region did you analyze, and what was the strategic question driving the case?",
        timestamp: tNow(0),
      },
    ],
    bullets: [],
  },
};

// Generic acknowledgement turn appended when the user sends a message after
// the fixture has loaded — keeps the chat reactive without a backend call.
export const DEMO_USER_REPLY_ACK = (userText) => ({
  id: `demo-ack-${Date.now()}`,
  role: 'assistant',
  content:
    "Got it — that's exactly the kind of detail that strengthens these bullets. In production, I'd weave this into the next bullet draft and ask one or two follow-ups to nail down the metric. (Demo mode — full back-and-forth runs in production.)",
  timestamp: new Date().toISOString(),
});
